import { type RouteRecordRaw } from 'vue-router';

const AccountLicenses = () => import('@/views/tenant/AccountLicenses.vue');
const DataGatewayAssignmentList = () =>
  import(
    '@/modules/dataGateway/components/assignmentList/AssignmentListContainer.vue'
  );
const DataGatewayAssignmentDetailsContainer = () =>
  import(
    '@/modules/dataGateway/components/assignmentDetails/AssignmentDetailsContainer.vue'
  );
const DepotsContainer = () =>
  import('@/components/configurations/DepotContainer.vue');
const DepotDetails = () =>
  import('@/components/configurations/depots/DepotDetails.vue');
const DepotCoreData = () =>
  import('@/components/configurations/depots/DepotCoreData.vue');
const DepotConfigurations = () =>
  import('@/components/configurations/depots/DepotConfigurations.vue');
const DepotsList = () =>
  import('@/components/configurations/depots/DepotList.vue');
const TenantContextContainer = () =>
  import('@/contexts/tenant/TenantContextContainer.vue');
const DashboardWrapper = () =>
  import('@/contexts/tenant/dashboards/DashboardWrapper.vue');
const DataGatewayJobDetailsContainer = () =>
  import('@/modules/dataGateway/components/jobDetails/JobDetailsContainer.vue');
const ParcelFinderContainer = () =>
  import('@/contexts/tenant/parcelFinder/ParcelFinderContainer.vue');
const ParcelDetails = () =>
  import('@/contexts/tenant/parcelFinder/ParcelDetails.vue');
const ParcelList = () =>
  import('@/contexts/tenant/parcelFinder/ParcelList.vue');
const RoleCreation = () =>
  import('@/contexts/tenant/roleCreation/RoleCreation.vue');
const AppAccountsContainer = () =>
  import('@/contexts/administration/appAccounts/AppAccountsContainer.vue');
const TenantUsersContainer = () =>
  import('@/contexts/administration/users/AdminUsersContainer.vue');
const UsersList = () => import('@/components/users/UsersList.vue');
const AddressLocationValidation = () =>
  import(
    '@/contexts/depot/addressLocationValidation/AddressLocationValidationContainer.vue'
  );
const HealthStatusContainer = () =>
  import('@/contexts/tenant/healthStatus/HealthStatusContainer.vue');

const ApiAccessManagement = () =>
  import('@/contexts/tenant/apiAccessManagement/ApiAccessManagement.vue');
const ApiAccessManagementDetails = () =>
  import(
    '@/contexts/tenant/apiAccessManagement/ApiAccessManagementDetails.vue'
  );
const KeyStatistics = () =>
  import('@/views/tenant/keyStatistics/KeyStatisticsContainer.vue');

const TenantContextNavigation = () =>
  import('@/contexts/tenant/TenantContextNavigation.vue');

const tenantChildRoutes: RouteRecordRaw[] = [
  {
    component: DashboardWrapper,
    name: 'tenantDashboards',
    path: 'dashboards/:dashboardName',
    props: true,
    meta: {
      fillHeight: true,
      titleKeyMap: {
        param: 'dashboardName',
        keys: {
          betterRouteReports: [
            'tenantContext.navigation.grouping.betterRoute',
            'generic.reports'
          ],
          betterTrackingReports: [
            'tenantContext.navigation.grouping.betterTracking',
            'generic.reports'
          ],
          cloudTrackingReports:
            'tenantContext.navigation.items.cloudTrackingReports',
          betterRouteGeocodingReports:
            'tenantContext.navigation.grouping.dgwReports',
          apiGeocodingReports:
            'tenantContext.navigation.grouping.deepIntReports'
        }
      }
    }
  },
  {
    component: RoleCreation,
    name: 'roleCreation',
    path: 'roles',
    props: true,
    meta: {
      titleKey: 'roles.roles'
    }
  },
  {
    component: ParcelFinderContainer,
    path: 'parcel-finder',
    props: true,
    meta: {
      titleKey: 'tenantContext.navigation.items.parcelFinder'
    },
    children: [
      {
        path: '',
        name: 'parcelList',
        component: ParcelList
      },
      {
        path: ':parcelId',
        name: 'parcelDetails',
        component: ParcelDetails,
        props: true
      }
    ]
  },
  {
    component: AppAccountsContainer,
    name: 'appAccounts',
    path: 'app-accounts',
    props: true,
    meta: {
      titleKey: 'mainNavigation.secondScreenAccounts'
    }
  },
  {
    component: AddressLocationValidation,
    name: 'tenantAddressLocationValidation',
    path: 'addressLocationValidation',
    props: true,
    meta: {
      titleKey: 'addressCorrections.addressCorrection'
    }
  },
  {
    children: [
      {
        components: {
          backofficeUsersTenant: UsersList
        },
        name: 'backofficeUsersTenant',
        path: 'backoffice',
        meta: {
          titleKey: 'userManagement.names.backofficeUser'
        }
      }
    ],
    component: TenantUsersContainer,
    name: 'tenantUsers',
    path: 'users',
    props: true
  },
  {
    component: DataGatewayAssignmentList,
    name: 'dataGatewayAssignmentListContainer',
    path: 'data-gateway/assignment-list/:date?',
    props: true,
    meta: {
      titleKey: 'mainNavigation.dataGatewayAssignmentList'
    }
  },
  {
    component: DataGatewayAssignmentDetailsContainer,
    name: 'dataGatewayAssignmentDetailsContainer',
    path: 'data-gateway/assignment-details/:date?/:internalDepotId?/:assignmentKey?/:assignmentValue?',
    props: true,
    meta: {
      titleKey: 'mainNavigation.dataGatewayAssignmentDetails'
    }
  },
  {
    component: DataGatewayJobDetailsContainer,
    name: 'dataGatewayJobDetailsContainer',
    path: 'data-gateway/job-details/:jobId?',
    props: true,
    meta: {
      titleKey: 'mainNavigation.dataGatewayJobDetails'
    }
  },
  {
    children: [
      {
        components: {
          configTenantDepots: DepotsList
        },
        name: 'configTenantDepots',
        path: '',
        props: true,
        meta: {
          titleKey: 'mainNavigation.contexts.depots'
        }
      },
      {
        children: [
          {
            components: {
              configTenantDepotCoreData: DepotCoreData
            },
            name: 'configTenantDepotCoreData',
            path: 'details',
            props: true,
            meta: {
              titleKey: 'mainNavigation.contexts.depots'
            }
          },
          {
            components: {
              configTenantDepotConfigurations: DepotConfigurations
            },
            name: 'configTenantDepotConfigurations',
            path: 'configurations',
            props: true,
            meta: {
              titleKey: 'mainNavigation.contexts.depots'
            }
          }
        ],
        components: {
          configTenantDepotDetails: DepotDetails
        },
        path: ':internalDepotId',
        props: true,
        meta: {
          titleKey: 'mainNavigation.contexts.depots'
        }
      }
    ],
    component: DepotsContainer,
    path: 'configurations/depots',
    props: true,
    meta: {
      titleKey: 'mainNavigation.contexts.depots'
    }
  },
  {
    component: AccountLicenses,
    name: 'accountLicenses',
    path: 'accounts/licenses',
    props: true,
    meta: {
      titleKey: 'tenantContext.navigation.items.bettermileAccountLicenses'
    }
  },
  {
    component: HealthStatusContainer,
    name: 'healthStatus',
    path: 'healthStatus',
    props: true,
    meta: {
      titleKey: 'healthStatus.label'
    }
  },
  {
    name: 'apiAccessManagement',
    path: 'api-access-management',
    component: ApiAccessManagement,
    meta: {
      titleKey: 'mainNavigation.apiAccessManagement'
    }
  },
  {
    name: 'apiAccessManagementDetails',
    path: 'api-access-management/:credentialsId',
    component: ApiAccessManagementDetails,
    props: true,
    meta: {
      titleKey: 'mainNavigation.apiAccessManagement'
    }
  },
  {
    component: KeyStatistics,
    name: 'keyStatistics',
    path: 'keyStatistics',
    props: true,
    meta: {
      titleKey: 'tenantContext.navigation.items.keyStatistics'
    }
  }
];

export const tenantRoute: RouteRecordRaw = {
  path: '/tenant/:tenantId?',
  components: {
    default: TenantContextContainer,
    LeftSidebar: TenantContextNavigation
  },
  props: {
    default: (route) => ({ tenantId: route.params.tenantId }),
    LeftSidebar: (route) => ({ tenantId: route.params.tenantId })
  },
  children: [
    {
      path: '',
      name: 'tenant',
      component: () => import('@/contexts/tenant/TenantSelectorCard.vue'),
      meta: {
        fillHeight: true,
        hasBasicLayout: true
      }
    },
    ...tenantChildRoutes
  ]
};
